import React from 'react';
import {
  makeStyles,
  Table,
  TableCell,
  TableRow,
  Theme,
  Typography,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Box,
  Card,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Page, GenericHeader, ODataGrid } from 'components/_dashboard';
import { IAuditTrail } from 'app/models/entities/IAuditTrail';
import { AuditTrailType } from 'app/enums/AuditTrailType';
import odataLookupObjFromEnum from 'utils/odataLookupObjFromEnum';
import { IProjectLearner } from 'app/models/responses/IProjectLearner';

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  diffHeading: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: theme.typography.fontWeightRegular as number,
  },
  diffTableCell: {
    wordWrap: 'break-word',
    maxWidth: 250,
  },
}));

const getPrimaryKey = (raw?: string | null) => {
  if (!raw) return 'N/A';
  const serialized: Record<'id', string> = JSON.parse(raw);
  return serialized.id;
};

const buildAffectedColumns = (raw?: string | null): React.ReactNode => {
  if (!raw) {
    return '-';
  }
  const serialized: string[] = JSON.parse(raw);
  return (
    <Box
      style={{ display: 'flex', alignItems: 'center', gap: '4px', width: 200, flexWrap: 'wrap' }}
    >
      {serialized.map((column) => (
        <Chip key={column} size='small' variant='default' color='secondary' label={column} />
      ))}
    </Box>
  );
};

interface IProps {
  projectLearner: IProjectLearner;
}

const History = ({ projectLearner }: IProps) => {
  const classes = useStyles();

  const buildDiffTable = (type: 'old' | 'new', raw?: string | null): React.ReactNode => {
    if (!raw) {
      return '-';
    }

    const values: Record<string, any> = JSON.parse(raw);
    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{type === 'old' ? 'View Old Values' : 'View New Values'}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Card variant='outlined'>
            <Table>
              <TableRow selected>
                <TableCell>Field</TableCell>
                <TableCell>Value</TableCell>
              </TableRow>
              {Object.keys(values).map((key) => (
                <TableRow key={key}>
                  <TableCell className={classes.diffTableCell}>{key}</TableCell>
                  <TableCell className={classes.diffTableCell}>{String(values[key])}</TableCell>
                </TableRow>
              ))}
            </Table>
          </Card>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      <ODataGrid<IAuditTrail>
        entityType='auditTrails'
        orderBy={['dateTime desc']}
        filter={{
          tableName: 'ProjectLearner',
          primaryKey: `{"id":${projectLearner.id}}`,
        }}
        options={{
          filterCellStyle: {
            minWidth: 200,
          },
          pageSizeOptions: [5, 10, 25],
          pageSize: 5,
        }}
        columns={[
          {
            field: 'type',
            title: 'Action',
            lookup: odataLookupObjFromEnum(AuditTrailType),
          },
          {
            field: 'oldValues',
            title: 'Old Values',
            render: (data) => buildDiffTable('old', data.oldValues),
          },
          {
            field: 'newValues',
            title: 'New Values',
            render: (data) => buildDiffTable('new', data.newValues),
          },
          {
            field: 'message',
            title: 'Reason Message',
          },
          {
            field: 'affectedColumns',
            title: 'Affected Columns',
            render: (data) => buildAffectedColumns(data.affectedColumns),
          },
          {
            field: 'userId',
            title: 'Action By',
          },
          {
            field: 'dateTime',
            title: 'Date',
            type: 'datetime',
            filtering: false,
            grouping: false,
          },
        ]}
      />
    </Box>
  );
};

export default History;
